// Copyright ©️ 2026 eVolve MEP, LLC

import type { Facility, Project, WorkOrderPaginated } from '@evolvemep/api-ts';
import { useDisclosure } from '@mantine/hooks';

import type { RequireAtLeastOne } from 'helpers/helper-types';
import { AddCartToWorkOrderModal } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/AddCartToWorkOrderModal';
import { BendCart } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/BendCart';
import { useBendCart } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import { NavPanel } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/NavPanel';
import { BendCreatorShell } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/BendCreatorShell';
import { useModelBend } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/useBendCreatorPreviewData';
import { useCopyWriteThrough } from 'modules/Shop/EvolveFabrication/BendCreator/State/useCopyEditing';

type Props = {
  onExit: () => void;
  /** Called once the bend list has been added to a work order. */
  onBendsCreated: () => void;
  selectedFacilities?: Facility[];
} & RequireAtLeastOne<{
  workOrder?: WorkOrderPaginated;
  selectedProjects?: Project[];
}>;

/**
 * The Bend Creator's create flow: build bends in the nav panel, collect them in the bend list, then add the list to a
 * work order. Must be rendered inside the Bend Creator's cart, form, validation, builder and preview providers.
 */
export const BendCreatorView = ({ onExit, onBendsCreated, ...workOrderProps }: Props) => {
  const { bendType, bendPreviewData } = useModelBend();
  const { bends } = useBendCart();
  useCopyWriteThrough();
  const [addingToWorkOrder, { open: openAddToWorkOrder, close: closeAddToWorkOrder }] = useDisclosure(false);

  return (
    <>
      <BendCreatorShell
        bendType={bendType}
        bendPreviewData={bendPreviewData}
        navPanel={<NavPanel />}
        cartPanel={<BendCart onAddToWorkOrder={openAddToWorkOrder} />}
        onClose={onExit}
        bendsToDiscard={bends.length}
      />
      <AddCartToWorkOrderModal
        opened={addingToWorkOrder}
        onClose={closeAddToWorkOrder}
        onBendsCreated={onBendsCreated}
        {...workOrderProps}
      />
    </>
  );
};
